/**
 * UI-8e Task 4 — TypeToConfirmDialog (shared helper).
 *
 * Centred modal for destructive actions where a single accept-click feels
 * too cheap. The operator must type a confirmation phrase verbatim before
 * the Delete button enables — typically the resource name (e.g. the flagKey
 * for a feature-flag deletion) so muscle-memory mis-clicks can't drop the
 * row.
 *
 * State:
 *   - `typed` mirrors the InputText value.
 *   - On close (`open` → false) the typed state clears so the next open
 *     starts fresh.
 *   - `matches` is a strict equality check (===) — no trim, no lowercase.
 *     This is intentional: typing "enable_test_feature " with a trailing
 *     space leaves Delete disabled.
 *
 * Accessibility:
 *   - The InputText has an aria-label naming the phrase the user must type.
 *   - A polite SR-only status announces when the phrase matches so screen
 *     reader users get the same "Delete became enabled" cue sighted users
 *     get visually.
 *
 * First consumer: FlagAccordionItem (UI-8e Task 7). Designed to be reused
 * by any future destructive admin flow.
 */
import { useEffect, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'

export interface TypeToConfirmDialogProps {
  open: boolean
  title: string
  message: string
  /** The exact phrase the user must type to enable Delete. */
  confirmText: string
  onConfirm: () => void
  onCancel: () => void
}

export function TypeToConfirmDialog({
  open,
  title,
  message,
  confirmText,
  onConfirm,
  onCancel,
}: TypeToConfirmDialogProps): JSX.Element {
  const [typed, setTyped] = useState('')

  // Clear typed state when the dialog closes so re-opens start fresh.
  useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  const matches = typed === confirmText

  return (
    <Dialog
      visible={open}
      onHide={onCancel}
      header={title}
      modal
      style={{ width: '32rem' }}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-700">{message}</p>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="type-to-confirm-input"
            className="text-sm font-medium"
          >
            Type{' '}
            <code className="rounded bg-gray-100 px-1">{confirmText}</code>{' '}
            to confirm
          </label>
          <InputText
            id="type-to-confirm-input"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value)
            }}
            placeholder={`Type "${confirmText}"`}
            aria-label={`Type "${confirmText}" to confirm`}
          />
          {/* SR-only live region — announces when Delete becomes enabled. */}
          <div role="status" aria-live="polite" className="sr-only">
            {matches ? 'Confirmation matches; Delete enabled' : ''}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            label="Cancel"
            text
            severity="secondary"
            onClick={onCancel}
          />
          <Button
            label="Delete"
            severity="danger"
            disabled={!matches}
            onClick={() => {
              if (matches) onConfirm()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default TypeToConfirmDialog
